<!-- components/IqMapCardIO.vue -->
<script setup>
import { ref, onMounted, watch } from "vue";
import { useElementVisibility } from "@vueuse/core";

const props = defineProps({
  mapSrc: { type: String, required: true },
  h2: { type: String, required: true },
  body: { type: String, default: "" },
  title: { type: String, default: "Map" },
});

const wrapperEl = ref(null);
const showIframe = ref(false);
const isVisible = useElementVisibility(wrapperEl, {
  rootMargin: "200px",
});

let preconnected = false;
function preconnectOrigin(urlStr) {
  if (preconnected) return;
  try {
    const u = new URL(urlStr);
    const origin = `${u.protocol}//${u.host}`;
    const l1 = document.createElement("link");
    l1.rel = "preconnect";
    l1.href = origin;
    l1.crossOrigin = "";
    const l2 = document.createElement("link");
    l2.rel = "dns-prefetch";
    l2.href = origin;
    document.head.appendChild(l1);
    document.head.appendChild(l2);
    preconnected = true;
  } catch {
    /* ignore */
  }
}

function loadIframeSoon() {
  console.log("Loading iframe soon...");
  if (showIframe.value) return;
  preconnectOrigin(props.mapSrc);
  const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 120));
  idle(() => {
    showIframe.value = true;
  });
}

onMounted(() => {
  // Fallback: if IO isn’t supported, just show it (still benefits from loading="lazy")
  if (!("IntersectionObserver" in window)) showIframe.value = true;
});

watch(isVisible, (v) => {
  if (v) loadIframeSoon();
});
</script>

<template>
  <div
    ref="wrapperEl"
    class="relative font-iq-paragraph leading-iq-paragraph tracking-iq-paragraph text-iq-paragraph-color"
  >
    <!-- Spacer to lock layout; no image placeholder -->
    <div
      v-if="!showIframe"
      class="w-full aspect-[25/9] md:rounded-iq-roundness bg-transparent"
    ></div>

    <iframe
      v-else
      class="md:rounded-iq-roundness w-full aspect-square md:aspect-[25/9]"
      width="100%"
      height="100%"
      frameborder="0"
      marginheight="0"
      marginwidth="0"
      :title="title"
      scrolling="no"
      :src="mapSrc"
      loading="lazy"
    ></iframe>

    <div
      class="md:absolute top-2 left-2 iq-card-solid md:w-[35%] p-4 md:p-10 -translate-y-16 md:-translate-y-0"
    >
      <h2
        class="font-semibold text-2xl font-iq-header leading-iq-header tracking-iq-header text-iq-header-color"
      >
        {{ h2 }}
      </h2>
      <slot name="body">
        <p class="mt-4 text-justify">{{ body }}</p>
      </slot>
    </div>
  </div>
</template>
